import { AlertCircle, RefreshCw } from 'lucide-react';
import { PropsWithChildren } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { useT } from '@/app/i18n/client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function ProductListItemContentErrorBoundary({ children }: PropsWithChildren) {
  return <ErrorBoundary FallbackComponent={FallbackComponent}>{children}</ErrorBoundary>;
}

const FallbackComponent = ({ error, resetErrorBoundary }: FallbackProps) => {
  const { t } = useT();

  return (
    <Card className='mx-auto w-full max-w-md'>
      <CardHeader className='text-center'>
        <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100'>
          <AlertCircle className='h-8 w-8 text-red-600' />
        </div>
        <CardTitle className='text-red-900'>{t('errorBoundary.title')}</CardTitle>
        <CardDescription>{t('errorBoundary.description')}</CardDescription>
      </CardHeader>

      <CardContent className='space-y-4'>
        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertTitle>{t('errorBoundary.errorDetails')}</AlertTitle>
          <AlertDescription className='break-words text-sm'>
            {error.message || t('errorBoundary.unknownError')}
          </AlertDescription>
        </Alert>

        <div className='space-y-2'>
          <Button onClick={resetErrorBoundary} className='w-full' size='lg'>
            <RefreshCw className='mr-2 h-4 w-4' />
            {t('errorBoundary.retryButton')}
          </Button>

          <Button
            variant='outline'
            onClick={() => window.location.reload()}
            className='w-full'
            size='lg'
          >
            {t('errorBoundary.refreshButton')}
          </Button>
        </div>

        <div className='pt-4 text-center'>
          <p className='text-xs text-muted-foreground'>{t('errorBoundary.supportMessage')}</p>
        </div>
      </CardContent>
    </Card>
  );
};
